import { useSelector } from 'react-redux'
import { selectActiveMedia } from 'store/selectors'
import { TRotationDirection } from './types'
import RotateIcon from 'assets/svgr-icons/Rotate.svg'
import FitIcon from 'assets/svgr-icons/Fit.svg'
import FillIcon from 'assets/svgr-icons/Fill.svg'
import FlipIcon from 'assets/svgr-icons/Flip_horizontal.svg'
import { RotateRightIcon } from './styled'
import { T } from 'consts/translationKeys'
import { dispatch } from 'store'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { calculateMediaRect } from 'utils/media'
import { FILL, FIT, ROTATION_FULL_CIRCLE, ROTATION_STEP } from 'consts/canvas'
import { TScaleMode } from 'types'

interface IMediaControl {
  id: string
  tooltipText?: T
  icon?: React.ReactNode
  onClick?: () => void
  type?: 'divider'
}

export const useMediaControls = () => {
  const activeMedia = useSelector(selectActiveMedia)
  const projectDefaultDimensions = useSelector(
    projectDataSelectors.defaultDimensions
  )

  const handleScale = (mode: TScaleMode = FILL) => {
    if (!activeMedia) return

    const { originalWidth: mediaWidth, originalHeight: mediaHeight } =
      activeMedia.meta
    const { width, height, x, y } = calculateMediaRect({
      mediaDimensions: { width: mediaWidth, height: mediaHeight },
      mode,
      canvasDimensions: projectDefaultDimensions,
      rotation: activeMedia.rotation
    })

    dispatch(
      projectDataThunks.updateMediaItem({
        id: activeMedia.id,
        updates: { width, height, coordinates: { x, y } }
      })
    )
  }

  const handleHorizontalFlip = () => {
    if (!activeMedia) return
    const currentFlip = activeMedia.flip || { x: false, y: false }

    dispatch(
      projectDataThunks.updateMediaItem({
        id: activeMedia.id,
        updates: { flip: { x: !currentFlip.x, y: false } }
      })
    )
  }

  const handleRotate = (direction: TRotationDirection) => {
    if (!activeMedia) return

    const currentRotation = activeMedia.rotation || 0
    const newRotation =
      direction === 'right'
        ? (currentRotation + ROTATION_STEP) % ROTATION_FULL_CIRCLE
        : (currentRotation - ROTATION_STEP + ROTATION_FULL_CIRCLE) %
          ROTATION_FULL_CIRCLE
    const { originalWidth: mediaWidth, originalHeight: mediaHeight } =
      activeMedia.meta
    const fitMode = calculateMediaRect({
      mediaDimensions: { width: mediaWidth, height: mediaHeight },
      mode: 'fit',
      canvasDimensions: projectDefaultDimensions,
      rotation: activeMedia.rotation
    })

    const currentMode =
      fitMode.width === activeMedia.width &&
      fitMode.height === activeMedia.height
        ? 'fit'
        : 'fill'

    const { x, y, width, height } = calculateMediaRect({
      mediaDimensions: {
        width: mediaWidth,
        height: mediaHeight
      },
      mode: currentMode,
      canvasDimensions: projectDefaultDimensions,
      rotation: newRotation
    })

    dispatch(
      projectDataThunks.updateMediaItem({
        id: activeMedia.id,
        updates: {
          rotation: newRotation,
          width,
          height,
          coordinates: { x, y }
        }
      })
    )
  }

  const mediaControls: IMediaControl[] = [
    {
      id: 'rotate-left',
      tooltipText: T.ROTATE_LEFT_LABEL,
      icon: <RotateIcon />,
      onClick: () => handleRotate('left')
    },
    {
      id: 'rotate-right',
      tooltipText: T.ROTATE_RIGHT_LABEL,
      icon: <RotateRightIcon />,
      onClick: () => handleRotate('right')
    },
    {
      id: 'divider',
      type: 'divider'
    },
    {
      id: 'fit',
      tooltipText: T.FIT_LABEL,
      icon: <FitIcon />,
      onClick: () => handleScale(FIT)
    },
    {
      id: 'fill',
      tooltipText: T.FILL_LABEL,
      icon: <FillIcon />,
      onClick: () => handleScale(FILL)
    },
    {
      id: 'flip-horizontal',
      tooltipText: T.FLIP_HORIZONTAL_LABEL,
      icon: <FlipIcon />,
      onClick: handleHorizontalFlip
    }
  ]

  return { mediaControls }
}
